import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/auth";
import { matchSupportedLocale } from "@/i18n/locales";
import { getProfile, putProfile } from "@/lib/profile-api";
import type { LangCode } from "@/lib/profile-api";

/** Maps a profile LangCode to the nearest GA i18n locale code (or null if none). */
function langCodeToI18n(code: LangCode): string | null {
  if (code === "zh-Hans") return "zh-CN";
  return matchSupportedLocale(code) ?? null;
}

/** Maps a GA i18n locale code back to a profile LangCode (or null if not a GA locale). */
function i18nToLangCode(lng: string): LangCode | null {
  if (lng === "zh-CN") return "zh-Hans";
  const passThrough: LangCode[] = ["en", "es", "hi", "tl", "tr"];
  return passThrough.includes(lng as LangCode) ? (lng as LangCode) : null;
}

/**
 * Bidirectional sync between i18next and the user's profile `native_language`.
 *
 * Profile → i18n: on first authentication, fetches the profile and applies
 * `native_language` as the active i18n locale (profile is authoritative for
 * the authenticated user's declared preference).
 *
 * i18n → profile: whenever the user changes language via the switcher,
 * persists it back to the profile so the preference is remembered across
 * devices and sessions.
 *
 * Both directions are best-effort; network failures are silently swallowed
 * because a language mismatch is never a blocking error.
 *
 * Mount once in App.tsx (inside AuthProvider + Router).
 */
export function useLanguageSync(): void {
  const { i18n } = useTranslation();
  const { state } = useAuth();
  const profileSyncedRef = useRef(false);

  // Profile → i18n direction: apply stored preference on first auth.
  useEffect(() => {
    if (state.kind !== "authenticated") {
      profileSyncedRef.current = false;
      return;
    }
    if (profileSyncedRef.current) return;
    profileSyncedRef.current = true;

    void (async () => {
      try {
        const profile = await getProfile();
        if (!profile) return;
        const target = langCodeToI18n(profile.native_language);
        if (target && target !== i18n.language) {
          void i18n.changeLanguage(target);
        }
      } catch {
        // Network failure or no profile yet — leave current language.
      }
    })();
  }, [state.kind, i18n]);

  // i18n → profile direction: save language changes back to the profile.
  useEffect(() => {
    if (state.kind !== "authenticated") return;

    function handleLanguageChanged(lng: string): void {
      const langCode = i18nToLangCode(lng);
      if (!langCode) return;
      void (async () => {
        try {
          const profile = await getProfile();
          if (!profile) return;
          await putProfile({
            home_country: profile.home_country,
            native_language: langCode,
            target_states: profile.target_states,
            current_focus_state: profile.current_focus_state,
            cdl_interest: profile.cdl_interest,
            ...(profile.persona !== undefined ? { persona: profile.persona } : {}),
          });
        } catch {
          // Best-effort — silent failure is acceptable.
        }
      })();
    }

    i18n.on("languageChanged", handleLanguageChanged);
    return () => {
      i18n.off("languageChanged", handleLanguageChanged);
    };
  }, [state.kind, i18n]);
}
